import { getDefaultOrFirstLang } from '@/models/language.model';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import { AxiosError } from 'axios';
import PageHeader from '@/components/PageHeader';
import { BaseErrorModel, PageState } from '@/models/core.model';
import { EnergyHub } from '@/models/energy-hub.model';
import { getEnergyHub } from '@/services/energy-hub.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import EnergyHubForm from './energy-hub-form';
import EnergyHubCreditPanel from './energy-hub-credit-panel';
import EnergyHubCreditLedger from './energy-hub-credit-ledger';
import EnergyHubOfferList from './energy-hub-offer-list';
import EnergyHubSlotEditor from './energy-hub-slot-editor';
import EnergyHubTagList from './energy-hub-tag-list';
import EnergyHubClubVisitList from './energy-hub-club-visit-list';
import EnergyHubVisitList from './energy-hub-visit-list';
import EnergyHubReceiptList from '../receipt/energy-hub-receipt-list';

const Page = () => {
  const [activeTab, setActiveTab] = useState('detail');
  const [formObject, setFormObject] = useState<EnergyHub | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [ledgerKey, setLedgerKey] = useState(0);
  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;

  useEffect(() => {
    if (id) {
      fetchData(true);
    } else {
      setFormObject(undefined);
      setActiveTab('detail');
    }
  }, [id]);

  const fetchData = async (showLoader = false) => {
    if (!id) return;
    if (showLoader) setLoading(true);
    try {
      const result = await getEnergyHub(id);
      if (result.success) setFormObject(result.result);
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching energy hub: ${errorModel?.errorMessage}`);
      nav('/energy-hubs');
    } finally {
      setLoading(false);
    }
  };

  // Fees, top-ups and receipt approvals all move the balance: refresh the summary and the ledger together.
  const handleCreditChanged = () => {
    fetchData();
    setLedgerKey(k => k + 1);
  };

  const newMode = pageState == PageState.New;

  return (
    <Panel
      header={
        <PageHeader title={!newMode && formObject ? `Energy Hub: ${getDefaultOrFirstLang(formObject.name) ?? ''}` : 'New Energy Hub'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href="/energy-hubs">Energy Hubs</Breadcrumb.Item>
            <Breadcrumb.Item active>{newMode ? 'New' : 'Edit'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <Nav appearance="subtle" activeKey={activeTab} onSelect={key => setActiveTab(key)}>
        <Nav.Item eventKey="detail">Detail</Nav.Item>
        <Nav.Item disabled={newMode} eventKey="offers">Offers</Nav.Item>
        <Nav.Item disabled={newMode} eventKey="slots">Hosting Slots</Nav.Item>
        <Nav.Item disabled={newMode} eventKey="tags">Check-in Tags</Nav.Item>
        <Nav.Item disabled={newMode} eventKey="hub-runs">Hub Runs</Nav.Item>
        <Nav.Item disabled={newMode} eventKey="visits">Visits</Nav.Item>
        <Nav.Item disabled={newMode} eventKey="receipts">Receipts</Nav.Item>
        <Nav.Item disabled={newMode} eventKey="credit">Credit</Nav.Item>
      </Nav>
      {loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'detail' && (
            <>
              {formObject && <EnergyHubCreditPanel hub={formObject} onChanged={handleCreditChanged} />}
              <EnergyHubForm formObject={formObject} pageState={pageState} onSaved={() => fetchData()} />
            </>
          )}
          {activeTab == 'offers' && formObject && <EnergyHubOfferList hub={formObject} onChanged={() => fetchData()} />}
          {activeTab == 'slots' && formObject && <EnergyHubSlotEditor hub={formObject} onChanged={() => fetchData()} />}
          {activeTab == 'tags' && formObject && <EnergyHubTagList hub={formObject} onChanged={() => fetchData()} />}
          {activeTab == 'hub-runs' && id && <EnergyHubClubVisitList energyHubId={id} />}
          {activeTab == 'visits' && id && <EnergyHubVisitList energyHubId={id} />}
          {activeTab == 'receipts' && id && <EnergyHubReceiptList energyHubId={id} onReviewed={handleCreditChanged} />}
          {activeTab == 'credit' && formObject && (
            <>
              <EnergyHubCreditPanel hub={formObject} onChanged={handleCreditChanged} />
              <EnergyHubCreditLedger energyHubId={formObject.id} reloadKey={ledgerKey} />
            </>
          )}
        </>
      }
    </Panel>
  );
};

export default Page;
